import React from 'react';
import { CheckIcon, MinusIcon } from '@heroicons/react/24/solid';
import clsx from 'clsx';

interface CheckboxProps {
  checked?: boolean;
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
  id?: string;
}

interface CheckboxGroupProps {
  label?: string;
  helperText?: string;
  isRequired?: boolean;
  error?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  label,
  helperText,
  isRequired = false,
  error = false,
  className,
  children
}) => {
  return (
    <fieldset className={clsx('flex flex-col', className)}>
      {label && (
        <legend className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </legend>
      )}
      <div className="space-y-2">
        {React.Children.map(children, child => {
          if (React.isValidElement<CheckboxProps>(child)) {
            return React.cloneElement(child, {
              error: error || child.props.error
            });
          }
          return child;
        })}
      </div>
      {helperText && (
        <p className={clsx('mt-2 text-sm', error ? 'text-red-500' : 'text-gray-500')}>
          {helperText}
        </p>
      )}
    </fieldset>
  );
};

export const Checkbox: React.FC<CheckboxProps> = ({
  checked = false,
  indeterminate = false,
  onChange,
  label,
  disabled = false,
  error = false,
  className,
  id,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && onChange) {
      onChange(e.target.checked);
    }
  };

  // Handle indeterminate state
  const checkboxRef = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  return (
    <div className={clsx('flex items-start', className)}>
      <div className="flex-shrink-0 mt-0.5">
        <input
          ref={checkboxRef}
          type="checkbox"
          id={id}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="sr-only peer"
        />
        <div
          onClick={() => !disabled && onChange?.(!checked)}
          className={clsx(
            'w-5 h-5 border rounded-sm transition-colors duration-200',
            'flex items-center justify-center',
            checked || indeterminate
              ? error
                ? 'bg-red-50 border-red-500'
                : 'bg-primary-base border-primary-base'
              : error
                ? 'border-red-300'
                : 'border-gray-300',
            !disabled && !checked && !indeterminate && error
              ? 'hover:border-red-400'
              : 'hover:border-primary-base',
            !disabled && 'cursor-pointer',
            disabled && 'opacity-50 cursor-not-allowed',
            'peer-focus:ring-2',
            error 
              ? 'peer-focus:ring-red-500/20'
              : 'peer-focus:ring-primary-base/20',
            'peer-focus:ring-offset-2'
          )}
        >
          {checked && !indeterminate && (
            <CheckIcon className={clsx('w-3.5 h-3.5', error ? 'text-red-500' : 'text-white')} />
          )}
          {indeterminate && (
            <MinusIcon className={clsx('w-3.5 h-3.5', error ? 'text-red-500' : 'text-white')} />
          )}
        </div>
      </div>
      {label && (
        <label
          htmlFor={id}
          className={clsx(
            'ml-2 text-sm inline-block',
            disabled ? 'text-gray-400' : error ? 'text-red-900' : 'text-gray-700',
            'cursor-pointer select-none'
          )}
        >
          {label}
        </label>
      )}
    </div>
  );
};

Checkbox.displayName = 'Checkbox';
CheckboxGroup.displayName = 'CheckboxGroup'; 